import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import { GetDeathCertificateById, UpdateDeathCertificate } from "../../../../api/ipd/deathCertificateAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
    ipdEntryId: yup.string().required("IPD Entry is required"),
    certificateNo: yup.string().required("Certificate No is required"),
    ipdNo: yup.string().required("IPD No is required"),
    dateOfIssue: yup.string().required("Date of Issue is required"),
    dateOfDeath: yup.string().required("date of death is required"),
    timeOfDeath: yup.string().required("Time of death is required"),
    causeOfDeath: yup.string().required("Cause of Death is required"),
    consultantId: yup.string().required("Consultant is required"),
});

const EditDeathCertificateEntry = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [ipdEntries, setIpdEntries] = useState([]);
    const [consultants, setConsultants] = useState([]);
    const [patientDetails, setPatientDetails] = useState({});

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {

        }
    });

    const fetchIpdEntries = async () => {
        try {
            const response = await GetIpdEntries();
            if (response.success) {
                setIpdEntries(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    const fetchConsultants = async () => {
        try {
            const response = await GetConsultants();
            if (response.success) {
                setConsultants(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    const fetchData = async () => {
        try {
            const response = await GetDeathCertificateById(id); // Make sure login function returns token
            if (response.success) {
                Object.entries(response.data).forEach(([key, value]) => {
                    // Handle main object dates
                    if (["dateOfIssue","dateOfDeath"].includes(key) && value) {
                        setValue(key, value.split("T")[0]);
                        return;
                    }
                    // Default
                    setValue(key, value);
                });

                setPatientDetails(response.data?.ipdEntryId || {});
                setValue('ipdEntryId', response.data?.ipdEntryId?._id || '');
                setValue('consultantId', response.data?.consultantId?._id || '');

            } else {
                // setError(response.message);
                showToast("error", response.message);
            }
        } catch (error) {
            // setError(error); // Handle login errors
            showToast("error", error);
        }
    };

    // Fetch existing data
    useEffect(
        () => {
            if (id) {
                fetchConsultants().then(() => {
                    fetchIpdEntries().then(() => {
                        fetchData();
                    });
                });
            }
        },
        [id, setValue]
    );

    const onSubmit = async data => {
        try {
            dispatch({ type: "LOADER_START" });

            const response = await UpdateDeathCertificate(id, data);
            if (response.success) {
                showToast("success", response.message);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const formRef = useRef(null);
      useKeyboardSubmit(handleSubmit, onSubmit);
      useEnterNavigation(formRef);

    return (
        <Fragment>
            <Breadcrumb parent="Death Certificate" title="Edit Death Certificate Entry" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between">
                                <h5>Edit Death Certificate Entry</h5>
                                <Link
                                    to={`${process.env
                                        .PUBLIC_URL}/ipd/transaction/death-certificate-entry/list`}
                                >
                                    <button className="btn btn-primary" id="list">
                                        List Death Certificate Entry
                                    </button>
                                    <UncontrolledTooltip placement="top" target="list">
                                        List Death Certificate Entry
                                    </UncontrolledTooltip>
                                </Link>
                            </div>

                            <form
                                ref={formRef}
                                className="form theme-form"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <div className="card-body">

                                    {/* ================= IPD & BASIC ================= */}
                                    <div className="row">
                                        <div className="col-6">
                                            <label>Room No</label>
                                            <select className="form-control" {...register("ipdEntryId", {
                                                onChange: (e) => {
                                                    setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                                                    setValue('ipdNo', ipdEntries.find(ipd => ipd._id === e.target.value)?.ipdNo);
                                                }
                                            })}>
                                                <option value="">Select IPD</option>
                                                {ipdEntries.map(ipd => (
                                                    <option key={ipd._id} value={ipd._id}>
                                                        {ipd.roomId?.roomNo}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.ipdEntryId && <span className="text-danger">{errors.ipdEntryId.message}</span>}
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Certificate No</label>
                                                <input
                                                    {...register("certificateNo")}
                                                    className="form-control"
                                                    placeholder="Certificate No"
                                                    readOnly
                                                />
                                                {errors.certificateNo && <span className="text-danger">{errors.certificateNo.message}</span>}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Ipd No</label>
                                                <input
                                                    {...register("ipdNo")}
                                                    className="form-control"
                                                    placeholder="Ipd No"
                                                    readOnly
                                                />
                                                {errors.ipdNo && <span className="text-danger">{errors.ipdNo.message}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Patient Details
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>UHID No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="UHID No"
                                                    value={patientDetails?.patientId?.uhidNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit Date</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit Date"
                                                    value={patientDetails?.date?.split("T")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit time</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit time"
                                                    value={patientDetails?.date?.split("T")[1].split(".")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Ipd No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Ipd No"
                                                    value={patientDetails?.ipdNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Patient</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.patientName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>guardian Name</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="guardian Name"
                                                    value={patientDetails?.patientId?.guardianName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Age</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.age}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Case</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Case"
                                                    value={patientDetails?.caseId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Consultant Doctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Consultant Doctor"
                                                    value={patientDetails?.consultantDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>ReferenceDoctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="ReferenceDoctor"
                                                    value={patientDetails?.referenceDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Address</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Address"
                                                    value={patientDetails?.patientId?.address}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* ================= DELIVERY DETAILS ================= */}
                                    <div className="row mt-3">
                                        <div className="col-4 mt-3">
                                            <label>Date Of Issue</label>
                                            <input type="date" className="form-control" {...register("dateOfIssue")} rows={5} />
                                            {errors.dateOfIssue && <span className="text-danger">{errors.dateOfIssue.message}</span>}
                                        </div>
                                        <div className="col-4 mt-3">
                                            <label>Date Of Death</label>
                                            <input type="date" className="form-control" {...register("dateOfDeath")} rows={5} />
                                            {errors.dateOfDeath && <span className="text-danger">{errors.dateOfDeath.message}</span>}
                                        </div>
                                        <div className="col-4 mt-3">
                                            <label>Time Of Death</label>
                                            <input type="time" className="form-control" {...register("timeOfDeath")} rows={5} />
                                            {errors.timeOfDeath && <span className="text-danger">{errors.timeOfDeath.message}</span>}
                                        </div>
                                        <div className="col-4 mt-3">
                                            <label>Cause Of Death</label>
                                            <textarea className="form-control" {...register("causeOfDeath")} rows={5} />
                                            {errors.causeOfDeath && <span className="text-danger">{errors.causeOfDeath.message}</span>}
                                        </div>
                                        <div className="col-4 mt-3">
                                            <label>Consultant Name</label>
                                            <select className="form-control" {...register("consultantId")}>
                                                <option value="">Select Consultant</option>
                                                {consultants.map((item) => (
                                                    <option value={item._id}>{item.name}</option>
                                                ))}
                                            </select>
                                            {errors.consultantId && <span className="text-danger">{errors.consultantId.message}</span>}
                                        </div>
                                    </div>
                                </div>

                                <div className="card-footer text-end">
                                    <button type="submit" className="btn btn-primary">
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default EditDeathCertificateEntry;
